/**
 * Code-aware error resolver for the finance mutations (pay / liquidate /
 * manual ledger). The API's error envelope is NOT uniform (confirmed with BE
 * 2026-07-01):
 *
 *  1. Business throws  → `{ errorCode, message, details? }`   → match `errorCode`
 *  2. DTO validation   → `{ statusCode, message: string[], error }` → no code
 *  3. Built-in 404s + the 429 throttle → `{ statusCode, message }`  → no code
 *
 * The shared `resolveMutationErrorMessage` is status-only, so it can't tell a
 * `PAYOUT_AUTO_TRANSFER_ON` 409 from any other 409. This resolver matches on
 * `errorCode` first, then falls back to `statusCode`, then to the server
 * message, then a generic. The translator is injected so it has no hook
 * dependency and stays unit-testable.
 *
 * Side-effect free — unit-tested in `resolve-payout-error.test.ts`.
 */
import { ApiError } from "@/shared/api/http-client";
import { extractApiErrorMessage } from "@/shared/api/error-message";

type Translate = (key: string, values?: Record<string, unknown>) => string;

/** Known business `errorCode`s → i18n keys under `finance.errors.*`. */
const CODE_KEYS: Readonly<Record<string, string>> = {
  PAYOUT_AUTO_TRANSFER_ON: "finance.errors.autoTransferOn",
  VENUE_NOT_FOUND: "finance.errors.venueNotFound",
  STRIPE_NOT_CONFIGURED: "finance.errors.stripeNotConfigured",
  INVALID_PAYOUT_STATUS: "finance.errors.invalidStatus",
};

/** Extract a business `errorCode` string from an ApiError body, if present. */
export function payoutErrorCode(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body;
  if (!body || typeof body !== "object") return null;
  const code = (body as { errorCode?: unknown }).errorCode;
  return typeof code === "string" && code.length > 0 ? code : null;
}

export function resolvePayoutError(err: unknown, t: Translate): string {
  const code = payoutErrorCode(err);
  if (code && CODE_KEYS[code]) return t(CODE_KEYS[code]);

  if (err instanceof ApiError) {
    if (err.status === 401 || err.status === 403) {
      return t("finance.errors.forbidden");
    }
    if (err.status === 429) return t("finance.errors.throttled");
    if (err.status === 503) return t("finance.errors.stripeNotConfigured");
    if (err.status === 404) return t("finance.errors.notFound");

    // Any other 4xx (validation array, unmapped business code) → surface the
    // server's own message so operators get the specific reason.
    if (err.status >= 400 && err.status < 500) {
      const serverMessage = extractApiErrorMessage(err);
      if (serverMessage) {
        return t("finance.errors.serverPrefix", { message: serverMessage });
      }
    }
  }

  return t("finance.errors.generic");
}
